import { z } from 'zod';
import { queryResultSchema } from './sandbox.js';
import { friendlyErrorSchema } from './sandbox.js';

/**
 * Grading contracts.
 *
 * A submission is judged on the ROWS IT RETURNS, never on its text. Two
 * structurally different queries that produce the same result are both correct,
 * which is the only defensible way to grade SQL.
 */

/** Machine-readable reasons a submission did not match. */
export const DIAGNOSTIC_CODES = {
  ROW_COUNT_MISMATCH: 'ROW_COUNT_MISMATCH',
  COLUMN_COUNT_MISMATCH: 'COLUMN_COUNT_MISMATCH',
  COLUMN_NAME_MISMATCH: 'COLUMN_NAME_MISMATCH',
  VALUE_MISMATCH: 'VALUE_MISMATCH',
  ORDERING_MISMATCH: 'ORDERING_MISMATCH',
  QUERY_FAILED: 'QUERY_FAILED',
} as const;
export type DiagnosticCode = (typeof DIAGNOSTIC_CODES)[keyof typeof DIAGNOSTIC_CODES];

export const diagnosticSchema = z.object({
  code: z.enum([
    'ROW_COUNT_MISMATCH',
    'COLUMN_COUNT_MISMATCH',
    'COLUMN_NAME_MISMATCH',
    'VALUE_MISMATCH',
    'ORDERING_MISMATCH',
    'QUERY_FAILED',
  ]),
  /** One sentence stating precisely what differs. */
  message: z.string(),
  expected: z.string().nullable(),
  actual: z.string().nullable(),
});
export type Diagnostic = z.infer<typeof diagnosticSchema>;

export const awardedBadgeSchema = z.object({
  slug: z.string(),
  name: z.string(),
  description: z.string(),
  icon: z.string(),
  /** XP the badge itself carried, separate from the exercise award. */
  xpReward: z.number().int(),
});
export type AwardedBadge = z.infer<typeof awardedBadgeSchema>;

export const submissionResponseSchema = z.object({
  correct: z.boolean(),
  /** Headline shown to the learner, e.g. "Correct" or "Not quite". */
  title: z.string(),
  /** The coaching paragraph. Always present, for right answers too. */
  feedback: z.string(),
  /** Specific, actionable next step when the answer is wrong. */
  hint: z.string().nullable(),
  diagnostics: z.array(diagnosticSchema),
  /** Optional style note on an otherwise correct answer. */
  styleNote: z.string().nullable(),
  /** Shown only once the exercise is solved. */
  explanationMd: z.string().nullable(),

  /** The learner's own result, so the grid can render it alongside feedback. */
  result: queryResultSchema.nullable(),
  error: friendlyErrorSchema.nullable(),

  // --- Rewards -------------------------------------------------------------
  /**
   * XP from solving the EXERCISE, after any hint penalty. Zero when already
   * solved. Badge XP is reported separately on each badge, so that "+3 XP" for
   * a hint-assisted solve is not silently inflated to "+13" by a badge that
   * happened to unlock at the same moment.
   */
  xpAwarded: z.number().int(),
  /** True the first time this exercise is solved. */
  firstSolve: z.boolean(),
  newBadges: z.array(awardedBadgeSchema),
  totalXp: z.number().int(),
  level: z.number().int(),
  levelTitle: z.string(),
  currentStreak: z.number().int(),
});
export type SubmissionResponse = z.infer<typeof submissionResponseSchema>;

export const submitRequestSchema = z.object({
  sql: z.string().min(1).max(10_000),
});
export type SubmitRequest = z.infer<typeof submitRequestSchema>;

// --- Hints ------------------------------------------------------------------

export const hintResponseSchema = z.object({
  position: z.number().int(),
  bodyMd: z.string(),
  xpPenalty: z.number().int(),
  hintsRemaining: z.number().int(),
});
export type HintResponse = z.infer<typeof hintResponseSchema>;
